import { Button, ButtonGroup, Icon } from '@qcx/ui';
import { MouseEventHandler, useCallback, useMemo } from 'react';
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { useNavigate } from 'react-router';
import { usePagination } from '../hooks/pagination.hook';

// TODO: Expose click handler to allow custom pagination logic

export interface PaginationProps {
  url: string;
  pagination: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    perPage: number;
  };
  pageUrlQueryParamName?: string;
  perPageUrlQueryParamName?: string;
  dots?: string;
  siblingCount?: number;
  permittedParams?: string[];
}

export const Pagination = ({
  url,
  pagination,
  dots = '...',
  siblingCount = 1,
  pageUrlQueryParamName = 'page',
  permittedParams
}: PaginationProps) => {
  const currentUrl = url;
  const navigate = useNavigate();
  const paginationRange = usePagination({
    currentPage: pagination.currentPage,
    totalCount: pagination.totalCount,
    pageSize: pagination.perPage,
    siblingCount
  });
  const prevPage = useMemo(
    () => pagination.currentPage - 1,
    [pagination.currentPage]
  );
  const nextPage = useMemo(
    () => pagination.currentPage + 1,
    [pagination.currentPage]
  );

  const hasPrevPage = useMemo(
    () => pagination.currentPage > 1,
    [pagination.currentPage]
  );
  const hasNextPage = useMemo(
    () => pagination.currentPage < pagination.totalPages,
    [pagination.currentPage, pagination.totalPages]
  );

  const getPath = useCallback(
    (page: number) => {
      const url = new URL(currentUrl);

      if (permittedParams) {
        const permittedParamsSet = new Set(permittedParams);

        for (const param of url.searchParams.keys()) {
          if (!permittedParamsSet.has(param)) {
            url.searchParams.delete(param);
          }
        }
      }

      if (page === 1) {
        url.searchParams.delete(pageUrlQueryParamName);
      } else {
        url.searchParams.set(pageUrlQueryParamName, page.toString());
      }

      return `${url.pathname}${url.search}`;
    },
    [currentUrl, pageUrlQueryParamName, permittedParams]
  );

  const handleNavigation = useCallback(
    (to: string): MouseEventHandler<HTMLButtonElement> =>
      e => {
        e.preventDefault();

        navigate(to);
      },
    [navigate]
  );

  // If there are less than 2 times in pagination range we shall not render the component
  if (pagination.currentPage === 0 || paginationRange.length < 2) {
    return null;
  }

  return (
    <ButtonGroup>
      <Button
        aria-label="prevpage"
        {...(hasPrevPage && { href: getPath(prevPage) })}
        as={hasPrevPage ? 'a' : 'button'}
        css={{ maxW: '$20' }}
        disabled={!hasPrevPage}
        icon={<Icon icon={faArrowLeft} />}
        iconButton
        onClick={handleNavigation(getPath(prevPage))}
      />
      {paginationRange.map(pageNumber => {
        if (pageNumber === dots) {
          return (
            <Button
              key={pageNumber}
              css={{
                '@xs': {
                  d: 'none'
                },
                pointerEvents: 'none'
              }}
            >
              &#8230;
            </Button>
          );
        }
        return (
          <Button
            key={pageNumber}
            as="a"
            css={{
              '@hover': {
                '&:hover': {
                  bgColor: '$neutral100 !important'
                }
              },
              '@xs': {
                d: 'none'
              },
              ...(pageNumber === pagination.currentPage
                ? {
                    bgColor: '$neutral50',
                    shadow: 'inset 0 0px 4px 0px rgb(0 0 0 / 10%)',
                    '@dark': {
                      bgColor: '$neutral700',
                      borderColor: '$neutral700'
                    }
                  }
                : {})
            }}
            href={getPath(Number(pageNumber))}
            onClick={handleNavigation(getPath(Number(pageNumber)))}
          >
            {pageNumber}
          </Button>
        );
      })}
      <Button
        aria-label="nextpage"
        {...(hasNextPage && { href: getPath(nextPage) })}
        as={hasNextPage ? 'a' : 'button'}
        css={{ maxW: '$20' }}
        disabled={!hasNextPage}
        icon={<Icon icon={faArrowRight} />}
        iconButton
        onClick={handleNavigation(getPath(nextPage))}
      />
    </ButtonGroup>
  );
};
